/**
 * The line of shot — the precision arc the equipped weapon draws, on the ground
 * in front of the character while aiming.
 *
 * Janeks: *"PVP/PVE UI, show line of shot, based on weapon stats."*
 *
 * ─── What is actually being drawn ───────────────────────────────────────────
 * Battleground Core Rule 4: every attack sweeps an arc whose length and width
 * come from the weapon plus the character's attributes. A melee swing damages
 * everything inside that wedge; a firearm fires one instant ray at a RANDOM
 * angle within it, which is the game's entire accuracy model — a tighter cone
 * is a more accurate gun.
 *
 * So the arc is not decoration. It is the literal shape the server tests hits
 * against, and a player who can see it can read their own weapon's spread
 * without a stat screen. A firearm additionally gets a centre line, because the
 * cone alone does not say which way the shot is nominally going.
 *
 * ─── The numbers are the server's ───────────────────────────────────────────
 * `AimArcSummary` arrives on the equipment sheet, resolved by the same function
 * the Colyseus room uses (`backend/src/game/battleground/aim-arc.ts`). Nothing
 * here derives geometry from item stats — F2 narrows the cone using
 * admin-tunable knobs, and a client-side copy would draw a promise the server
 * had stopped keeping.
 *
 * ─── Geometry, and the sign that is easy to get wrong ───────────────────────
 * `arcWidth` is the FULL cone angle in DEGREES; `THREE.RingGeometry` wants
 * radians, and measures theta counter-clockwise from local +X in its own XY
 * plane.
 *
 * Worked through rather than guessed, because both plausible signs draw a
 * convincing arc and one of them points out of the character's back:
 *
 *   rotation.x = -PI/2  maps local (x, y, 0) -> world (x, 0, -y)
 *   a ring point at theta is local (r cos t, r sin t, 0)
 *                              -> world (r cos t, 0, -r sin t)
 *
 * For that to land on world **+Z** — "forward", the zero of this codebase's yaw
 * convention — we need `-sin t = 1`, i.e. `t = -PI/2`. So the sweep is centred
 * on `-PI/2`, NOT `+PI/2`.
 *
 * With the wedge built pointing at +Z, the group's yaw is then applied
 * positively (`rotation.y = facing`), exactly like every other object in the
 * scene — `sceneHandles.player.rotation.y = snap.player.facing`. Two mistakes
 * that cancel would also look right at facing 0 and be wrong everywhere else,
 * which is why `arcGeometry` is exported and asserted directly.
 */
import * as THREE from 'three';

// One shape, declared where the wire is declared — a local copy here would be a
// second definition of the same server payload.
import type { AimArcSummary } from '../../state/game-state.types';

export type { AimArcSummary };

export interface AimArcUpdate {
  visible: boolean;
  arc: AimArcSummary | null;
  x: number;
  z: number;
  /** Player yaw, radians, 0 = +Z — the same convention `BaseSim` uses. */
  facing: number;
}

/**
 * Just above the ground, under everything else that hugs it.
 *
 * Roads sit at `ROAD_Y` and decals step up from there in 0.01 increments, so
 * this deliberately sits below the lot of them: the arc is a HUD element that
 * happens to be drawn in the world, and it reading "under" a road is correct —
 * it must never look like something painted on top of the scenery.
 */
const ARC_Y = 0.02;

/** Radial segments across the cone. Enough that a 120° melee sweep has no visible facets. */
const ARC_SEGMENTS = 48;

/**
 * Inner radius, metres — the arc starts clear of the character's own feet
 * rather than at a point, so a wide melee wedge does not collapse into a
 * solid disc under the body.
 */
const ARC_INNER = 0.35;

const COLOR_FIREARM = 0xffd9a0; // warm brass — reads as a sight line
const COLOR_MELEE = 0xd9a8a8; // dulled rust — reads as reach, not a beam
const COLOR_UNARMED = 0x9fb0a4; // muted moss — deliberately the least assertive

function colorFor(kind: AimArcSummary['kind']): number {
  if (kind === 'FIREARM') return COLOR_FIREARM;
  if (kind === 'MELEE') return COLOR_MELEE;
  return COLOR_UNARMED;
}

export interface AimArcHandles {
  object: THREE.Object3D;
  update(next: AimArcUpdate): void;
  dispose(): void;
}

/**
 * Builds the wedge for one arc.
 *
 * Exported and pure so the geometry can be asserted in a test without a WebGL
 * context — the sign convention above is exactly the kind of thing that looks
 * right in code and points backwards on screen.
 */
export function arcGeometry(arcLength: number, arcWidthDeg: number): THREE.RingGeometry {
  const theta = THREE.MathUtils.degToRad(Math.max(0.1, arcWidthDeg));
  const outer = Math.max(ARC_INNER + 0.01, arcLength);
  // Centred on theta = -PI/2, which is world +Z once the ring is laid flat —
  // see the derivation in this file's header.
  return new THREE.RingGeometry(ARC_INNER, outer, ARC_SEGMENTS, 1, -Math.PI / 2 - theta / 2, theta);
}

export function createAimArc(): AimArcHandles {
  const group = new THREE.Group();
  group.visible = false;
  // The arc is an overlay: it must not be picked, must not catch fog, and must
  // not be occluded by the ground it is lying on.
  group.renderOrder = 18;

  const material = new THREE.MeshBasicMaterial({
    color: COLOR_FIREARM,
    transparent: true,
    opacity: 0.16,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: false,
    fog: false,
  });

  const edgeMaterial = new THREE.LineBasicMaterial({
    color: COLOR_FIREARM,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
    depthTest: false,
    fog: false,
  });

  let wedge: THREE.Mesh | null = null;
  const centreLine = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
    edgeMaterial,
  );
  centreLine.renderOrder = 19;
  centreLine.visible = false;
  group.add(centreLine);

  // Rebuild only when the SHAPE changes. The arc is recomputed by the server on
  // every equip, but between equips it is constant while the player waves the
  // cursor around — rebuilding a RingGeometry every frame would allocate and
  // upload a new buffer sixty times a second for an unchanged shape.
  let builtLength = -1;
  let builtWidth = -1;
  let builtKind: AimArcSummary['kind'] | null = null;

  const rebuild = (arc: AimArcSummary): void => {
    if (wedge) {
      group.remove(wedge);
      wedge.geometry.dispose();
    }
    wedge = new THREE.Mesh(arcGeometry(arc.arcLength, arc.arcWidth), material);
    wedge.rotation.x = -Math.PI / 2;
    wedge.renderOrder = 18;
    // Never a click target — `picking.ts` raycasts the scene, and an invisible
    // 40-metre disc under the cursor would swallow every interaction.
    wedge.raycast = () => {};
    group.add(wedge);

    const color = colorFor(arc.kind);
    material.color.setHex(color);
    edgeMaterial.color.setHex(color);

    // The centre line is the nominal shot direction. Meaningless for a melee
    // sweep (the whole wedge lands at once) and for bare hands, so it is drawn
    // for firearms only.
    const show = arc.kind === 'FIREARM';
    centreLine.visible = show;
    if (show) {
      centreLine.geometry.dispose();
      centreLine.geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0, ARC_INNER),
        new THREE.Vector3(0, 0, Math.max(ARC_INNER + 0.01, arc.arcLength)),
      ]);
    }

    builtLength = arc.arcLength;
    builtWidth = arc.arcWidth;
    builtKind = arc.kind;
  };

  return {
    object: group,
    update(next: AimArcUpdate): void {
      if (!next.visible || !next.arc) {
        group.visible = false;
        return;
      }
      const arc = next.arc;
      if (arc.arcLength !== builtLength || arc.arcWidth !== builtWidth || arc.kind !== builtKind) {
        rebuild(arc);
      }
      group.visible = true;
      group.position.set(next.x, ARC_Y, next.z);
      // Positive, like every other object in the scene — the wedge is already
      // built pointing at +Z. See the header's derivation.
      group.rotation.y = next.facing;
    },
    dispose(): void {
      if (wedge) {
        wedge.geometry.dispose();
        group.remove(wedge);
        wedge = null;
      }
      centreLine.geometry.dispose();
      material.dispose();
      edgeMaterial.dispose();
      group.clear();
    },
  };
}
